/**
 * Poseidon dashboard — operator sign-in (§3.1).
 *
 * §3.1 requires a local operator login before any command endpoint is available,
 * because local malware, browser extensions or another local user must not be
 * able to submit orders. The daemon owns authentication; this page only collects
 * credentials and hands them over.
 *
 * In mock mode there is no authentication at all, and the page says so rather
 * than implying a session was verified.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useServices } from '../state/Services';
import { IS_MOCK_MODE } from '../config';
import { AiDisclaimer, Badge, Panel } from '../components/primitives';

export function Login(): JSX.Element {
  const { login, sessionLoading, sessionError } = useServices();
  const [operator, setOperator] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setLocalError(null);
    if (operator.trim().length === 0) {
      setLocalError('An operator name is required so commands can be attributed in the audit ledger.');
      return;
    }
    try {
      await login(operator.trim(), password);
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Sign-in failed.');
    }
  };

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={(event) => void submit(event)}>
        <div className="sidebar__brand" style={{ padding: 0 }}>
          <span className="sidebar__brand-name">POSEIDON</span>
          <span className="sidebar__brand-sub">operator sign-in</span>
        </div>

        <p className="text-muted" style={{ margin: 0, fontSize: 12.5 }}>
          This dashboard controls a local MT5 <strong>demo</strong> workstation. Sign-in is required even on localhost: another local
          process with a browser must not be able to reach the command endpoints.
        </p>

        {IS_MOCK_MODE && (
          <div className="banner banner--mock" style={{ borderRadius: 6, border: '1px solid #6b5210' }}>
            <span className="banner__title">⚠ mock mode</span>
            <span>No authentication is performed. Any operator name is accepted and no credential is verified.</span>
          </div>
        )}

        <label>
          Operator
          <input
            type="text"
            value={operator}
            autoComplete="username"
            spellCheck={false}
            onChange={(event) => setOperator(event.target.value)}
          />
        </label>

        <label>
          Password
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {(localError !== null || sessionError !== null) && (
          <div className="dialog__error" role="alert">
            {localError ?? sessionError}
          </div>
        )}

        <button type="submit" className="btn btn--primary" disabled={sessionLoading}>
          {sessionLoading ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="data-clock">
          <Badge tone="neutral" outline>
            localhost only
          </Badge>
          <Badge tone="neutral" outline>
            demo accounts only
          </Badge>
          <Badge tone="neutral" outline>
            CSRF token issued per session
          </Badge>
        </div>

        <Panel title="Session handling" subtitle="what the daemon must do">
          <ul className="consequences" style={{ paddingLeft: 18 }}>
            <li>Issue a SameSite session cookie and a CSRF token from <span className="text-mono">GET /api/auth/session</span>.</li>
            <li>Require the <span className="text-mono">x-poseidon-csrf</span> header on every command request (§16.4.1).</li>
            <li>Re-verify authority, risk state and the typed confirmation phrase server-side — this dialog is a safeguard, not a security boundary.</li>
            <li>Track secrets and credentials only in the daemon's secret store; nothing sensitive reaches this bundle (§11).</li>
          </ul>
        </Panel>

        <AiDisclaimer />
      </form>
    </div>
  );
}
